<template>
  <div>
    <v-container class="pt-3 pb-6 px-6">

      <!-- Floating Button -->
      <floating-button @click="farmCreate()" v-if="authUser && !isClient && !isReadonly"/>

      <!-- Confirmation -->
      <confirmation
        v-if="authUser"
        v-model="dialog"
        title="Eliminar Parque Eólico"
        text="Desea eliminar el parque eólico"
        :item="farmToDelete.name"
        @close="dialog = false"
        @confirm="deleteFarm()"
      />

      <!-- Page Header -->
      <page-header
        title="Parques Eólicos"
        icon="mdi-map-marker"
        divider="-"
        :breadcrumbs="breadcrumbs.items"
        refresh
        @refresh="loadFarms()"
      ></page-header>

      <!-- Content -->
      <v-row align="center" justify="center">
        <!-- Table -->
        <v-col cols="12">
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Table -->
          <v-data-table
            v-if="authUser"
            :headers="headers"
            :items="filteredBlade"
            :items-per-page="5"
            class="elevation-1"
            :search="filterInput"
            :expanded.sync="expanded"
            :footer-props="{
              'items-per-page-options': [5, 15, 30, 50]
            }"
          >
            <!-- Top -->
            <template v-slot:top>
              <v-toolbar flat color="white">
                <!-- State Filter -->
                <v-select
                  v-if="!isClient"
                  v-model="filterState"
                  :items="stateItems"
                  class="shrink mr-5"
                  label="Estado"
                  clearable
                  hide-details
                  single-line
                ></v-select>
                <!-- Turbine Filter -->
                <v-autocomplete
                  v-model="filterTurbine"
                  :items="turbineList"
                  label="Fabricante-Modelo-AG"
                  class="grow mr-5"
                  :item-text="text"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                ></v-autocomplete>
                <!-- Blade Filter -->
                <v-autocomplete
                  v-model="filterBlade"
                  :items="bladeList"
                  label="Fabricante-Modelo-Palas"
                  class="grow mr-5"
                  :item-text="text"
                  item-value="id"
                  clearable
                  single-line
                  hide-details
                ></v-autocomplete>
                <!-- Input Filter -->
                <v-text-field
                  v-model="filterInput"
                  class="grow"
                  label="Buscar..."
                  prepend-icon="mdi-magnify"
                  clearable
                  single-line
                  hide-details
                ></v-text-field>
              </v-toolbar>
            </template>
            <!-- Actions -->
            <template v-slot:item.action="{ item }">
              <v-icon class="mr-2" @click="farmEdit(item.id)">mdi-pencil</v-icon>
              <v-icon @click="deleteFarmDialog(item)">mdi-delete</v-icon>
            </template>
            <!-- Expand Icon -->
            <template v-slot:item.expand="{ item }">
              <v-icon
                :class="{'rotate': expanded.includes(item)}"
                icon
                @click="expanded.includes(item) ? expanded = [] : expanded = [item]"
                v-if="item.wind_turbine_types.length > 1 || item.wind_turbine_blade_types.length > 1"
              >mdi-chevron-down</v-icon>
            </template>
            <!-- Expanded Row -->
            <template v-slot:expanded-item="{ headers, item }">
              <!-- Turbines -->
              <td :colspan="headers.length / 2" v-if="item.wind_turbine_types.length > 1" class="text-center py-3">
                <div class="mb-3 font-weight-bold caption">Fabricante-Modelo-AG</div>
                <span
                  class="mx-1"
                  v-for="(turbine, idx) in item.wind_turbine_types"
                  :key="idx"
                >
                  <v-chip class="my-1 text-capitalize" v-if="idx >= 1">{{ turbine.manufacturer + ' - ' + turbine.model }}</v-chip>
                </span>
              </td>
              <!-- Blades -->
              <td :colspan="headers.length / 2" v-if="item.wind_turbine_blade_types.length > 1" class="text-center py-3">
                <div class="mb-3 font-weight-bold caption">Fabricante-Modelo-Palas</div>
                <span
                  class="mx-1"
                  v-for="(blade, idx) in item.wind_turbine_blade_types"
                  :key="idx"
                >
                  <v-chip class="my-1 text-capitalize" v-if="idx >= 1">{{ blade.manufacturer + ' - ' + blade.model }}</v-chip>
                </span>
              </td>
            </template>
            <!-- Turbine Type -->
            <template v-slot:item.wind_turbine_types="{ item }">
              <div
                v-for="(turbine, idx) in item.wind_turbine_types"
                :key="idx"
              >
                <v-chip class="my-1 text-capitalize" v-if="idx < 1">{{ turbine.manufacturer + ' - ' + turbine.model }}</v-chip>
              </div>
              <div class="pb-1" v-if="item.wind_turbine_types.length > 1" >({{ item.wind_turbine_types.length - 1 }} más)</div>
            </template>
            <!-- Turbine Blade Type -->
            <template v-slot:item.wind_turbine_blade_types="{ item }">
              <div
                v-for="(blade, idx) in item.wind_turbine_blade_types"
                :key="idx"
                >
                  <v-chip class="my-1 text-capitalize" v-if="idx < 1">{{ blade.manufacturer + ' - ' + blade.model }}</v-chip>
              </div>
              <div class="pb-1" v-if="item.wind_turbine_blade_types.length > 1" >({{ item.wind_turbine_blade_types.length - 1 }} más)</div>
            </template>
            <!-- State -->
            <template v-slot:item.is_active="{ item }">
              <v-chip
                :color="item.is_active ? 'primary' : 'deep-orange'"
                :class="{'white--text': !item.is_active}"
              >{{ item.is_active ? 'Activo' : 'Inactivo' }}</v-chip>
            </template>
            <!-- No Data -->
            <template v-slot:no-data>
              No hay elementos
            </template>
            <!-- No Results -->
            <template v-slot:no-results>
              No hay elementos
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import Confirmation from "@/components/Confirmation";
import PageHeader from "@/components/PageHeader";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    Confirmation,
    PageHeader
  },
  data: () => ({
    expanded: [],
    authUser: false,
    isLoadingData: false,
    dialog: false,
    farmToDelete: {},
    filterInput: '',
    filterState: null,
    filterTurbine: '',
    filterBlade: '',
    filterItems: [
      {
        text: 'Todos',
        value: 'all'
      },
      {
        text: 'Nombre del Parque',
        value: 'name'
      },
      {
        text: 'Propiedad del Parque',
        value: 'property'
      },
      {
        text: 'Fabricante-Modelo-AG',
        value: 'turbine'
      },
      {
        text: 'Fabricante-Modelo-Pala',
        value: 'blade'
      },
      {
        text: 'Estado',
        value: 'state'
      }
    ],
    stateItems: [
      {
        text: 'Activo',
        value: 'true'
      },
      {
        text: 'Inactivo',
        value: 'false'
      }
    ],
    breadcrumbs: {
      items: [
        {
          text: "Configuración",
          disabled: true
        },
        {
          text: "Parques Eólicos",
          disabled: true
        }
      ]
    },
    defaultHeaders: [
      {
        text: '',
        align:'center',
        sortable: false,
        value: 'expand'
      },
      {
        text: 'Nombre del parque',
        align: 'center',
        sortable: true,
        value: 'name',
        client: true
      },
      {
        text: 'Propiedad del parque',
        align: 'center',
        sortable: true,
        value: 'owner'
      },
      {
        text: 'Número de AG',
        align: 'center',
        sortable: true,
        value: 'number_of_wind_turbines',
        client: true
      },
      {
        text: 'Fabricante-Modelo-AG',
        align: 'center',
        sortable: true,
        value: 'wind_turbine_types',
        client: true
      },
      {
        text: 'Fabricante-Modelo-Palas',
        align: 'center',
        sortable: true,
        value: 'wind_turbine_blade_types',
        client: true
      },
      {
        text: 'Estado',
        align: 'center',
        sortable: true,
        value: 'is_active'
      },
      {
        text: 'Acción',
        align: 'center',
        sortable: false,
        value: 'action'
      }
    ]
  }),
  computed: {
    isClient () {
      return this.$store.getters['IS_CLIENT']
    },
    isReadonly() {
      return this.$store.getters['IS_READONLY']
    },
    turbineList () {
      return this.$store.state.farms.turbineList
    },
    bladeList () {
      return this.$store.state.farms.bladeList
    },
    farms () {
      return this.$store.state.farms.wind_farms
    },
    headers () {
      return this.isReadonly
        ? this.defaultHeaders.filter(e => e.value !== 'action')
        : this.isClient
          ? this.defaultHeaders.filter(e => e.client)
          : this.defaultHeaders
    },
    filteredState () {
      return this.filterState
        ? this.farms.filter(element => {
          return element.is_active.toString().includes(this.filterState)
        }) : this.farms
    },
    filteredTurbine () {
      return this.filterTurbine
        ? this.filteredState.filter(element => {
            return element.wind_turbine_types.some(element => {
              return element.id === this.filterTurbine
            })
          }) : this.filteredState
    },
    filteredBlade () {
      return this.filterBlade
        ? this.filteredTurbine.filter(element => {
          return element.wind_turbine_blade_types.some(element => {
            return element.id === this.filterBlade
          })
        }) : this.filteredTurbine
    }
  },
  methods: {
    text (item) {
      return item.manufacturer + ' - ' + item.model
    },
    deleteFarmDialog (farm) {
      this.farmToDelete = farm
      this.dialog = !this.dialog
    },
    deleteFarm () {
      var id = this.farmToDelete.id
      this.$store.dispatch('farms/deleteFarm', id)
        .then(() => {
          this.$toast("Parque eólico borrado correctamente.", {
            color: "success",
            x: "center",
            y: "top",
            icon: "mdi-information"
          });
          this.loadFarms()
        })
        .catch(error => {
          var errorToastMessage = "Ha ocurrido un error eliminando el parque."
          Util.showErrorToast(this, error, errorToastMessage);
        })
        .finally(() => {
          this.dialog = !this.dialog
        })
    },
    farmCreate () {
      this.$router.push({  name: "farm-create" });
    },
    farmEdit (id) {
      this.$router.push({
        name: "farm-edit",
        params: { id: id }
      });
    },
    loadFarms () {
      this.isLoadingData = true;
      this.$store.dispatch('farms/getTurbineList')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de turbinas."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
      this.$store.dispatch('farms/getBladeList')
        .then(() => {})
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de palas."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
      this.$store.dispatch('farms/getFarms')
        .then(() => {
          this.authUser = true
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando los parques eólicos."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.isLoadingData = false;
        });
    }
  },
  created () {
    this.loadFarms()
  }
}
</script>
